<template>
	<div class="portal-top-nav">
		<header class="portal-header">
			<div class="brand-zone" :class="{ 'is-collapsed': !$store.state.leftNavExpanded }">
				<button type="button" class="brand-link" :aria-label="accountName" @click="$router.push('/welcome')">
					<img v-if="logo" :src="logo" :alt="accountName" class="brand-logo" />
					<span v-else class="brand-fallback" aria-hidden="true">{{ accountInitial }}</span>
				</button>
				<button
					type="button"
					class="collapse-control d-none d-sm-flex"
					:aria-label="$store.state.leftNavExpanded ? $t('nav.collapse-navigation') : $t('nav.expand-navigation')"
					:content="$store.state.leftNavExpanded ? $t('nav.collapse-navigation') : $t('nav.expand-navigation')"
					v-tippy="{ placement: 'right', boundary: 'window' }"
					@click="toggleLeftNav"
				>
					<portal-icon
						:name="$store.state.leftNavExpanded ? 'panel-left-close' : 'panel-left-open'"
						:size="19"
					></portal-icon>
				</button>
			</div>

			<div class="header-navigation">
				<tabs v-if="hasContextNavigation" :tab-config="currentTabsConfig" :current-path="currentPath" />
			</div>

			<div class="header-actions">
				<div class="d-none d-sm-flex">
					<button
						type="button"
						class="header-action"
						:class="{ active: $store.state.rightNavExpanded && $store.state.rightNavFocus === 'notifications' }"
						:aria-label="$t('nav.notifications')"
						@click="openRightNav('notifications')"
					>
						<portal-icon name="bell" :size="21"></portal-icon>
						<span class="notification-count" v-if="$store.state.actionCount">{{ $store.state.actionCount }}</span>
					</button>
				</div>

				<v-menu v-if="mobile" offset-y left content-class="portal-notification-menu">
					<template v-slot:activator="{ on, attrs }">
						<button type="button" class="header-action" :aria-label="$t('nav.notifications')" v-bind="attrs" v-on="on">
							<portal-icon name="bell" :size="21"></portal-icon>
							<span class="notification-count" v-if="$store.state.actionCount">{{ $store.state.actionCount }}</span>
						</button>
					</template>
					<div class="mobile-notifications">
						<notifications></notifications>
					</div>
				</v-menu>

				<v-menu offset-y left content-class="user-menu">
					<template v-slot:activator="{ on, attrs }">
						<button type="button" class="profile-button" :aria-label="$t('nav.open-profile-menu')" v-bind="attrs" v-on="on">
							<span class="profile-avatar">{{ initials }}</span>
							<span class="profile-name d-none d-sm-inline">{{ firstName }}</span>
						</button>
					</template>
					<user-menu></user-menu>
				</v-menu>
			</div>
		</header>

	</div>
</template>

<script>
	import Tabs from '@/views/nav/Tabs.vue';
	import UserMenu from '@/views/nav/UserMenu';
	import NavConfig from '@/views/nav/NavConfig.json';
	import Notifications from '@/views/nav/Notifications';
	import NavMixin from '@/views/nav/NavMixin';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'TopNav',

		components: { Notifications, UserMenu, Tabs, PortalIcon },

		mixins: [NavMixin],

		data() {
			return {
				navConfig: NavConfig,
				currentTabsConfig: { tabs: [], title: '' },
				currentPath: null,
				mobile: window.innerWidth <= 599,
			};
		},

		created() {
			window.addEventListener('resize', this.checkScreen);
			this.restoreLeftNavState();
			this.restoreRightNavState();
		},

		beforeDestroy() {
			window.removeEventListener('resize', this.checkScreen);
		},

		methods: {
			checkScreen() {
				this.mobile = window.innerWidth <= 599;
			},

			toggleLeftNav() {
				this.$store.state.leftNavExpanded = !this.$store.state.leftNavExpanded;
				localStorage.setItem(
					'LEFT_NAV_STATE',
					JSON.stringify({ state: this.$store.state.leftNavExpanded })
				);
			},

			restoreLeftNavState() {
				try {
					const savedState = JSON.parse(localStorage.getItem('LEFT_NAV_STATE'));
					if (savedState && typeof savedState.state === 'boolean') {
						this.$store.state.leftNavExpanded = savedState.state;
					}
				} catch (error) {
					localStorage.removeItem('LEFT_NAV_STATE');
				}
			},

			restoreRightNavState() {
				try {
					const savedState = JSON.parse(localStorage.getItem('RIGHT_NAV_STATE'));
					if (savedState) {
						this.$store.state.rightNavExpanded = Boolean(savedState.rightNavExpanded);
						this.$store.state.rightNavFocus = savedState.rightNavFocus || null;
					}
				} catch (error) {
					localStorage.removeItem('RIGHT_NAV_STATE');
				}
			},

			openRightNav(focus) {
				const isCurrentPanel =
					this.$store.state.rightNavExpanded && this.$store.state.rightNavFocus === focus;
				this.$store.state.rightNavExpanded = !isCurrentPanel;
				this.$store.state.rightNavFocus = isCurrentPanel ? null : focus;
				localStorage.setItem(
					'RIGHT_NAV_STATE',
					JSON.stringify({
						rightNavExpanded: this.$store.state.rightNavExpanded,
						rightNavFocus: this.$store.state.rightNavFocus,
					})
				);
			},

			updateRouteContext(route) {
				this.currentPath = route.fullPath;
				const parts = route.path.split('/');
				const rootPath = parts.length > 1 ? `/${parts[1]}` : '';
				this.currentTabsConfig = this.navConfig[rootPath]
					? { ...this.navConfig[rootPath] }
					: { tabs: [], title: '' };
			},
		},

		watch: {
			$route: {
				deep: true,
				immediate: true,
				handler(route) {
					this.updateRouteContext(route);
				},
			},
		},

		computed: {
			hasContextNavigation() {
				const hasDefaultCustomPage =
					this.$route.name === 'Welcome' && this.$store.state.customPages.some((page) => page.isDefault);
				if (this.$route.meta.hideContextNavigation || hasDefaultCustomPage) {
					return false;
				}
				return Boolean(
					this.currentTabsConfig.title ||
					(this.currentTabsConfig.tabs && this.currentTabsConfig.tabs.length)
				);
			},

			accountName() {
				return this.$store.state.domain && this.$store.state.domain.accountName
					? this.$store.state.domain.accountName
					: 'Moxie';
			},

			accountInitial() {
				return this.accountName.substring(0, 1).toUpperCase();
			},

			firstName() {
				return this.$store.state.loggedInUser && this.$store.state.loggedInUser.firstName
					? this.$store.state.loggedInUser.firstName
					: '';
			},

			initials() {
				const user = this.$store.state.loggedInUser || {};
				return `${user.firstName ? user.firstName.substring(0, 1) : ''}${
					user.lastName ? user.lastName.substring(0, 1) : ''
				}`.toUpperCase();
			},
		},
	};
</script>

<style scoped lang="scss">
	.portal-top-nav {
		position: relative;
		z-index: 20;
		flex: 0 0 auto;
		background: var(--portal-surface);
	}

	.portal-header {
		position: relative;
		min-height: var(--portal-header-height);
		display: grid;
		grid-template-columns: var(--v-left-nav-width) minmax(0, 1fr) auto;
		background: var(--portal-surface);
		transition: grid-template-columns 180ms ease;
	}

	.portal-header::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: var(--v-left-nav-width);
		right: 0;
		border-bottom: 1px solid var(--portal-border);
		pointer-events: none;
	}

	.brand-zone,
	.brand-link,
	.header-actions,
	.profile-button,
	.collapse-control {
		display: flex;
		align-items: center;
	}

	.brand-zone {
		position: relative;
		min-width: 0;
		gap: 6px;
		padding: 10px 18px 10px 14px;
		border-right: 1px solid var(--portal-nav-border);
		background: var(--v-sidebar-base);
	}

	.brand-link {
		width: 100%;
		height: 44px;
		min-width: 0;
		overflow: hidden;
		padding: 0;
		border: 0;
		background: transparent;
		color: var(--portal-nav-text);
		cursor: pointer;
		text-align: left;
		border-radius: var(--brand-button-radius, 12px);
	}

	.brand-fallback {
		width: 36px;
		height: 36px;
		flex: 0 0 36px;
		border-radius: 12px;
	}

	.brand-logo {
		display: block;
		width: auto;
		height: auto;
		max-width: min(190px, 100%);
		max-height: 42px;
		object-fit: contain;
		object-position: left center;
		filter: drop-shadow(0 4px 9px rgba(16, 17, 15, 0.08));
	}

	.brand-fallback {
		display: grid;
		place-items: center;
		background: var(--v-primary-base);
		color: var(--v-white-base);
		font-weight: 600;
	}

	.collapse-control,
	.header-action,
	.profile-button {
		border: 0;
		background: transparent;
		color: var(--portal-muted);
		cursor: pointer;
	}

	.collapse-control {
		position: absolute;
		z-index: 4;
		top: 50%;
		right: -17px;
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		justify-content: center;
		border: 1px solid var(--portal-border);
		border-color: color-mix(in srgb, var(--portal-border) 72%, transparent);
		border-radius: var(--brand-button-radius, 11px);
		background: var(--portal-surface);
		background: color-mix(in srgb, var(--portal-surface) 48%, transparent);
		box-shadow: 0 3px 10px rgba(16, 17, 15, 0.1);
		transform: translateY(-50%);
		transition:
			background-color 160ms ease,
			border-color 160ms ease,
			color 160ms ease,
			transform 160ms ease;
	}

	.header-actions {
		justify-self: end;
		gap: 4px;
		padding: 10px 18px;
	}

	.header-action {
		position: relative;
		width: 40px;
		height: 40px;
		display: grid;
		place-items: center;
		border-radius: var(--brand-button-radius, 10px);
	}

	.header-action.active,
	.header-action:hover,
	.collapse-control:hover,
	.profile-button:hover {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.collapse-control:hover {
		border-color: var(--portal-border);
		border-color: color-mix(in srgb, var(--v-primary-base) 28%, var(--portal-border));
		transform: translate(1px, -50%);
	}

	.notification-count {
		position: absolute;
		top: 2px;
		right: 1px;
		min-width: 18px;
		height: 18px;
		display: grid;
		place-items: center;
		padding: 0 5px;
		border: 2px solid var(--portal-surface);
		border-radius: 99px;
		background: var(--v-error-base);
		color: var(--v-white-base);
		font-size: 11px;
		font-weight: 600;
	}

	.profile-button {
		gap: 8px;
		min-height: 40px;
		padding: 4px 8px 4px 5px;
		border-radius: var(--brand-button-radius, 12px);
		color: var(--portal-text);
	}

	.profile-avatar {
		width: 32px;
		height: 32px;
		display: grid;
		place-items: center;
		border-radius: 50%;
		background: var(--v-primary-base);
		color: var(--v-white-base);
		font-weight: 600;
	}

	.profile-name {
		font-weight: 500;
	}

	.header-navigation {
		min-width: 0;
		align-self: center;
		padding: 0 8px 0 32px;
	}

	.header-navigation ::v-deep .module-title {
		flex-shrink: 1;
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
	}

	.header-navigation ::v-deep .tabs-wrapper {
		flex-shrink: 0;
	}

	.mobile-notifications {
		width: min(360px, calc(100vw - 24px));
		height: min(620px, calc(100vh - 96px));
		overflow: auto;
		background: var(--portal-surface);
	}

	@media (min-width: 600px) {
		.brand-zone.is-collapsed {
			justify-content: center;
			padding-right: 10px;
			padding-left: 10px;
		}

		.brand-zone.is-collapsed .brand-link {
			width: 36px;
			height: 36px;
			margin-right: auto;
			margin-left: auto;
		}

		.brand-zone.is-collapsed .brand-logo {
			width: 36px;
			height: 36px;
			max-width: 36px;
			max-height: 36px;
			object-position: center;
		}
	}

	@media (max-width: 599px) {
		.header-navigation {
			display: none;
		}

		.portal-header {
			grid-template-columns: minmax(0, 1fr) auto;
		}

		.brand-zone {
			border-right: 0;
			padding: 9px 14px;
			background: var(--portal-surface);
		}

		.header-actions {
			padding: 9px 12px 9px 0;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.portal-header,
		.collapse-control {
			transition: none;
		}
	}
</style>
